import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Shield, Award, Users, ArrowRight, Heart } from 'react-feather';

const AboutUs = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1586024486164-ce9b3d87e0d3?q=80&w=2000&auto=format&fit=crop"
            alt="Pharmacy Storefront"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/90 to-primary-600/40" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl text-white"
          >
            <div className="inline-flex items-center space-x-2 bg-primary-500/20 backdrop-blur-md border border-primary-400/30 rounded-full px-4 py-2 mb-6">
              <Award className="h-4 w-4 text-primary-200" />
              <span className="text-sm font-semibold tracking-wide uppercase">Your Trusted Health Partner</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              About <br />
              <span className="text-primary-200">Our Pharmacy</span>
            </h1>
            <p className="text-xl text-primary-50 mb-8 leading-relaxed">
              Serving our community with integrity, care, and professional expertise for over a decade.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact & Location Section */}
      <section className="py-20 -mt-20 relative z-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Column: Contact Info */}
            <div className="lg:col-span-5">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="space-y-8"
              >
                <motion.div variants={itemVariants} className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100">
                  <h2 className="text-3xl font-bold text-gray-900 mb-8">Contact Information</h2>
                  
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-primary-50 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0">
                        <MapPin className="h-6 w-6 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 uppercase font-bold tracking-wider mb-1">Our Location</p>
                        <p className="text-lg text-gray-800 font-semibold">123 Health Street, Medical District, Nairobi, Kenya</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-primary-50 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0">
                        <Phone className="h-6 w-6 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 uppercase font-bold tracking-wider mb-1">Phone Number</p>
                        <p className="text-lg text-gray-800 font-semibold">+254 700 000 000</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-primary-50 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0">
                        <Mail className="h-6 w-6 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 uppercase font-bold tracking-wider mb-1">Email Address</p>
                        <p className="text-lg text-gray-800 font-semibold">contact@maxmapharmacy.com</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="w-12 h-12 bg-primary-50 rounded-2xl flex items-center justify-center mr-4 flex-shrink-0">
                        <Clock className="h-6 w-6 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 uppercase font-bold tracking-wider mb-1">Working Hours</p>
                        <p className="text-lg text-gray-800 font-semibold">Mon - Fri: 8:00 AM - 9:00 PM</p>
                        <p className="text-lg text-gray-800 font-semibold">Sat - Sun: 9:00 AM - 6:00 PM</p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Values Card */}
                <motion.div variants={itemVariants} className="bg-gradient-to-br from-primary-600 to-primary-700 p-8 rounded-3xl shadow-xl text-white">
                  <h3 className="text-2xl font-bold mb-6">Our Core Values</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4">
                      <Shield className="h-8 w-8 mb-3 text-primary-200" />
                      <p className="font-bold">Trust</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4">
                      <Heart className="h-8 w-8 mb-3 text-primary-200" />
                      <p className="font-bold">Care</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4">
                      <Users className="h-8 w-8 mb-3 text-primary-200" />
                      <p className="font-bold">Community</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4">
                      <Award className="h-8 w-8 mb-3 text-primary-200" />
                      <p className="font-bold">Excellence</p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>

            {/* Right Column: Map & Mission */}
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="space-y-8"
              >
                {/* Mission Section */}
                <div className="bg-gray-50 p-8 md:p-12 rounded-3xl border border-gray-100">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 italic">"To provide accessible, high-quality pharmaceutical care and health advice that empowers our community to live healthier, happier lives."</h2>
                  <p className="text-lg text-gray-600 leading-relaxed mb-6">
                    Established in 2012, Maxma Pharmacy has grown from a small local dispensary to a comprehensive health hub. 
                    We believe that healthcare should be personalized, empathetic, and always professional.
                  </p>
                  <button className="inline-flex items-center text-primary-600 font-bold hover:text-primary-700 transition-colors">
                    Join Our Community <ArrowRight className="ml-2 h-5 w-5" />
                  </button>
                </div>

                {/* Map Integration (Placeholder for real Google Maps) */}
                <div className="h-[400px] bg-gray-200 rounded-3xl overflow-hidden relative shadow-inner group">
                   <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?q=80&w=1000&auto=format&fit=crop')] bg-cover bg-center grayscale group-hover:grayscale-0 transition-all duration-700"></div>
                   <div className="absolute inset-0 bg-primary-900/10 group-hover:bg-transparent transition-all duration-700"></div>
                   <div className="absolute inset-0 flex items-center justify-center">
                     <div className="bg-white/90 backdrop-blur-md px-6 py-4 rounded-2xl shadow-2xl border border-primary-100 flex items-center space-x-3">
                       <MapPin className="text-primary-600 h-6 w-6 animate-bounce" />
                       <span className="font-bold text-gray-900">Find us here</span>
                     </div>
                   </div>
                </div>
              </motion.div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
